import { ChangeEvent, useState } from "react";
import {
  useAddChildCollectionMutation,
  useCreateCollectionMutation,
  useGetConnectionsQuery,
  useSetCollectionProviderMutation,
} from "@/api/api";
import { FolderPlus, Plus } from "lucide-react";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog";
import { Input } from "../input";
import { Checkbox } from "../checkbox";
import { Field, FieldLabel } from "../field";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { SidebarGroupAction } from "../sidebar";
import { useIsOwnSpace } from "@/api/hooks";
import { HGCollection } from "@/common/types";
import { pluralize } from "@/common/utils";
import { getProvider, providerLibrary } from "@/providers/external/registry";

export default function CollectionCreate({
  className,
  parent,
}: {
  /** Laid out in the flow of the list header, not in `SidebarGroupAction`'s
   * absolute corner slot — the header owns its own row of controls. */
  className?: string;
  /**
   * What to offer to file the new collection under, or null for none. Decided
   * by the list rather than read from the URL here: what makes sense is the
   * collection the user is *looking at*, which is only sometimes the open one.
   */
  parent?: HGCollection | null;
}) {
  const [open, setOpen] = useState(false);
  const me = useAppSelector(selectMe);
  const isOwnSpace = useIsOwnSpace();

  const [createCollection] = useCreateCollectionMutation();
  const [addChildCollection] = useAddChildCollectionMutation();
  const [setCollectionProvider] = useSetCollectionProviderMutation();
  const { data: connections } = useGetConnectionsQuery(undefined, {
    skip: !isOwnSpace,
  });
  const [title, setTitle] = useState<string>("");
  const [asChild, setAsChild] = useState(true);
  // Which library entry the new collection mirrors, or null for an ordinary
  // one. Picking one prefills the name, which is almost always what it should
  // be called.
  const [providerKey, setProviderKey] = useState<string | null>(null);

  if (!me || !isOwnSpace) return null;

  const provider = providerKey ? getProvider(providerKey) : null;
  const existingConnection = connections?.find(
    (connection) =>
      connection.system_id === provider?.systemId &&
      connection.status === "active" &&
      // A grant that predates this provider may not cover what it needs; the
      // collection then shows the connect prompt, and the incremental consent
      // screen lists only the new scope.
      provider.scopes.every((scope) => connection.scopes.includes(scope)),
  );

  const pickProvider = (key: string | null) => {
    setProviderKey(key);
    const picked = key ? getProvider(key) : null;
    // Only overwrite an untouched field: someone who typed a name and then
    // chose a provider meant the name.
    if (
      picked &&
      (title.trim() === "" || providerLibrary.some((p) => p.title === title))
    ) {
      setTitle(picked.title);
    }
  };

  const handleCreate = async () => {
    if (title.trim().length === 0) return;
    try {
      const { id } = await createCollection({
        title,
        ...(provider
          ? {
              provider_key: provider.key,
              provider_config: {},
              // The natural subcollections travel with the root so the whole
              // set is created in one transaction -- a Gmail collection with
              // no categories is not a half-finished thing the user can fix.
              provider_children: provider.naturalChildren.map((child) => ({
                title: child.title,
                provider_key: child.providerKey,
                provider_config: child.config,
              })),
            }
          : {}),
      }).unwrap();
      if (asChild && parent) {
        await addChildCollection({
          parentID: parent.id,
          childIDs: [id],
        });
      }
      // Reuse a grant the user already gave on this system. Asking again for
      // permission they have already granted is the thing this whole feature
      // exists not to do.
      if (provider && existingConnection) {
        await setCollectionProvider({
          collectionID: id,
          connection_id: existingConnection.id,
          set_connection: true,
        });
      }
    } catch {
      // No error UI here -- the dialog closes either way; a failed create
      // just means nothing new shows up.
    }
    setTitle("");
    setProviderKey(null);
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTitle("");
      setProviderKey(null);
    }
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <SidebarGroupAction className={className}>
          <Plus />
        </SidebarGroupAction>
      </DialogTrigger>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
        >
          <DialogHeader>
            <DialogTitle>New collection</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Field>
              <FieldLabel htmlFor="collectionTitle">Name</FieldLabel>
              <Input
                id="collectionTitle"
                value={title}
                placeholder="Enter collection name"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setTitle(e.target.value)
                }
                autoFocus
              />
            </Field>

            {/* The library. An external system is an ordinary collection with a
                provider attached, which is why this is one more choice in the
                same dialog rather than a separate flow. */}
            {providerLibrary.length > 0 && (
              <div className="mt-4">
                <FieldLabel className="mb-2 block">Mirror a system</FieldLabel>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => pickProvider(null)}
                    className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm ${
                      providerKey === null
                        ? "border-primary"
                        : "border-transparent hover:bg-row-hover"
                    }`}
                  >
                    <FolderPlus className="h-4 w-4 shrink-0" />
                    <span>Just a collection</span>
                  </button>
                  {providerLibrary.map((entry) => {
                    const Icon = entry.Icon;
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        onClick={() => pickProvider(entry.key)}
                        className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-left text-sm ${
                          providerKey === entry.key
                            ? "border-primary"
                            : "border-transparent hover:bg-row-hover"
                        }`}
                      >
                        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                        <span className="min-w-0">
                          <span className="block">{entry.title}</span>
                          <span className="block text-xs text-muted-foreground">
                            {entry.description}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {provider && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {/* Not every system has a natural partition to offer:
                        Calendar's is the account's own list of calendars,
                        which nothing here can know before the grant. */}
                    {provider.naturalChildren.length > 0 &&
                      `Adds ${pluralize(
                        provider.naturalChildren.length,
                        "subcollection",
                      )}. `}
                    You will be asked to grant access before anything is shown.
                  </p>
                )}
              </div>
            )}

            {parent && (
              <Field orientation="horizontal" className="mt-3">
                <Checkbox
                  id="collectionAsChild"
                  checked={asChild}
                  onCheckedChange={(checked) => setAsChild(checked === true)}
                />
                <FieldLabel htmlFor="collectionAsChild" className="font-normal">
                  Make child of &ldquo;{parent.title}&rdquo;
                </FieldLabel>
              </Field>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={title.trim().length === 0}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
